import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormGroupDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { debounceTime, distinctUntilChanged, Observable, startWith, Subject, take, takeUntil } from 'rxjs';
import { UtilsService } from '../../../../../shared/services/utils.service';
import { SharedService } from '../../../../../shared/services/shared.service';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { BankruptciesFilterRequest, ClientBusinessEntityControllerService, ManagementBusinessEntityControllerService, ManagementBusinessEntityResponse } from 'api-contract-notices';
import { MatOption, MatOptionModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

@Component({
	selector: 'app-bankruptcies-filters',
	standalone: true,
	imports: [
		MatCardModule,
		CommonModule,
		ReactiveFormsModule,
		FormsModule,
		MatFormFieldModule,
		TranslateModule,
		MatDatepickerModule,
		MatToolbarModule,
		MatIconModule,
		MatProgressSpinnerModule,
		MatButtonModule,
		MatDividerModule,
		MatTooltipModule,
		MatAutocompleteModule,
		MatOptionModule,
		MatInputModule,
		MatSelectModule,
	],
	templateUrl: './bankruptcies-filters.component.html',
	styleUrl: './bankruptcies-filters.component.scss',
})
export class BankruptciesFiltersComponent implements OnInit, OnChanges, OnDestroy {
	private onDestroy: Subject<boolean> = new Subject();

	@Input() role!: 'admin' | 'client' | 'landing';
	@Input() dataAdministrationType!: 'BANKRUPTCIES';
	@Input() resetFilters: any;
	@Input() apiLoading!: boolean;
	@Output() actionRequested: EventEmitter<any> = new EventEmitter();

	readonly INSTANT_SEARCH = false; // setting option to have search without button
	filtersFormGroup: FormGroup = new FormGroup({});

	DAYS_RANGE = 0;
	DATE_RANGE_LIMITS = {
		dateFrom: new Date(new Date().setDate(new Date().getDate() - this.DAYS_RANGE)),
		dateTo: new Date(),
	};

	filteredCompanies: ManagementBusinessEntityResponse[] | undefined;

	bankruptcyPhases: BankruptciesFilterRequest.PhasesEnum[] = [
		'PRE_BANKRUPTCY',
		'BANKRUPTCY_PROCEEDINGS_OPEN',
		'CREDITORS_ASSEMBLY',
		'BANKRUPTCY_PROCEEDINGS_OPEN_CREDITORS_ASSEMBLY',
		'FILING_IN_BANKRUPTCY_FILE',
		'BANKRUPTCY_PROCEEDINGS_CLOSE',
		'BANKRUPTCY_PROCEEDINGS_OPEN_CLOSE',
		'SUSPEND_PRE_BANKRUPTCY',
		'PROPERTY_SALE',
		'PROPERTY_ASSESSMENT',
		'PROCUREMENT_SERVICES_GOODS',
		'OTHER',
	];
	@ViewChild('selectAllPhases') private selectAllPhases!: MatOption;

	constructor(
		private fb: FormBuilder,
		public utils: UtilsService,
		private sharedService: SharedService,
		private apiBusinessEntity: ManagementBusinessEntityControllerService,
		private apiClientBusinessEntity: ClientBusinessEntityControllerService
	) {}

	ngOnInit(): void {
		this.formInitializer();

		this.sharedService.requestActionInComponent.pipe(takeUntil(this.onDestroy)).subscribe({
			next: response => {
				if (response.componentName === 'BankruptciesFiltersComponent') {
					switch (response.action) {
						case 'FILTER_TRIGGER': {
							this.filterListItems();
							break;
						}
					}
				}
			},
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['resetFilters'] && !changes['resetFilters'].currentValue && !changes['resetFilters'].firstChange) {
			this.resetForm();
		}
		if (changes['dataAdministrationType'] && !changes['dataAdministrationType'].firstChange) {
			this.resetForm();
		}
		if (changes['role'] && changes['role'].currentValue === 'client') {
			this.DAYS_RANGE = 7;
			this.DATE_RANGE_LIMITS = {
				dateFrom: new Date(new Date().setDate(new Date().getDate() - this.DAYS_RANGE)),
				dateTo: new Date(),
			};
		}
	}

	ngOnDestroy(): void {
		this.onDestroy.next(true);
	}

	private formInitializer() {
		this.filtersFormGroup = this.fb.group({
			createdAtFrom: [this.DATE_RANGE_LIMITS.dateFrom],
			createdAtTo: [this.DATE_RANGE_LIMITS.dateTo],
			companyId: [],
			phases: [[]],
		});

		this.formControlWatchers();
	}

	phaseSelection(itemSelected?: Event) {
		if (itemSelected) {
			if (this.selectAllPhases.selected) {
				this.selectAllPhases.deselect();
			} else if (this.filtersFormGroup.controls['phases'].value.length === this.bankruptcyPhases.length) {
				this.selectAllPhases.select();
			}
		} else {
			this.filtersFormGroup.controls['phases'].patchValue(this.selectAllPhases.selected ? ['ALL', ...this.bankruptcyPhases] : [], 0);
		}
	}

	private formControlWatchers() {
		if (this.filtersFormGroup.controls['companyId']) {
			this.filtersFormGroup.controls['companyId'].valueChanges.pipe(startWith(''), debounceTime(450), distinctUntilChanged(), takeUntil(this.onDestroy)).subscribe(searchTerm => {
				if (this.filtersFormGroup.controls['companyId'].dirty) {
					this.searchBusinessEntities(searchTerm);
				}
			});
		}
	}

	resetForm(formDirective?: FormGroupDirective) {
		this.filtersFormGroup.reset();

		if (formDirective) {
			formDirective.resetForm();
		}

		this.filtersFormGroup.controls['createdAtFrom'].setValue(this.DATE_RANGE_LIMITS.dateFrom);
		this.filtersFormGroup.controls['createdAtTo'].setValue(this.DATE_RANGE_LIMITS.dateTo);

		this.filterListItems();
	}

	filterListItems() {
		const filterDto = JSON.parse(JSON.stringify(this.filtersFormGroup.value));
		filterDto.createdAtFrom = this.utils.setDateToDay(this.filtersFormGroup.controls['createdAtFrom'].value, 'START');
		filterDto.createdAtTo = this.utils.setDateToDay(this.filtersFormGroup.controls['createdAtTo'].value, 'END');
		filterDto.companyId = this.filtersFormGroup.controls['companyId'].value?.id;
		const selectedPhases: string[] = this.filtersFormGroup.controls['phases'].value || [];
		filterDto.phases = selectedPhases.filter(p => p !== 'ALL');
		this.actionRequested.emit({ action: 'FILTER', filters: filterDto, id: 0 });
	}

	private getBusinessEntitySearchObservable(searchTerm: string): Observable<any> {
		switch (this.role) {
			case 'admin':
				return this.apiBusinessEntity.getBusinessEntitiesPageable({ businessEntity: searchTerm }, 0, 100);
			case 'client':
				return this.apiClientBusinessEntity.getClientBusinessEntitiesPageable({ businessEntity: searchTerm }, 0, 100);
			default:
				return this.apiBusinessEntity.getBusinessEntitiesPageable({ businessEntity: searchTerm }, 0, 100);
		}
	}

	private searchBusinessEntities(searchTerm: string) {
		if (searchTerm && searchTerm.length > 0) {
			this.getBusinessEntitySearchObservable(searchTerm)
				.pipe(take(1))
				.subscribe({
					next: response => {
						this.filteredCompanies = response.content;
					},
				});
		} else {
			if (this.INSTANT_SEARCH) {
				this.filterListItems();
			}
		}
	}
	displayFn(company: ManagementBusinessEntityResponse): string {
		return company && company.id ? this.utils.getParameterByLanguageScript(company, 'name', true) : '';
	}
}
